import React, {useCallback, useEffect, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/mobile/lib/sendAnalyticsEvent/index';
import {Modal, pushSnackInfo, RoundButton, zIndex} from '@optimaxdev/design-system';
import {Approve, Icon} from '@optimaxdev/icons';
import {connect} from 'react-redux';

import {
    CART_UPGRADE_SAME_FRAME_USAGE,
    CART_UPGRADE_UPGRADES_LEARN_MORE,
    CART_UPGRADE_UPGRADES_USAGE,
} from 'constants/analytics/events/cartUpgrade';
import {ApplicationStoreType} from 'desktop/store';

import {setUpsellPopupContent} from '../../store/reducers/upsells/upsells';
import {getCartItemById} from '../../store/selectors';

import {PropsType} from './types';
import {UpsellContent} from './upsellContent/upsellContent';

import s from './upsell.scss';

const getEventLabel = (title: string, isLearnMore: boolean) => {
    if (title.toLowerCase().includes('1-year')) return '1-year insurance';
    if (title.toLowerCase().includes('2-year')) return '2-year insurance';
    if (title === 'Enhanced Coverage' && !isLearnMore) return 'One year insurance';
    if (title === 'Enhanced Coverage' && isLearnMore) return 'Insurance';
    return title;
};

/**
 * Component UpsellOfferItem in cart upsells
 *  @param {PropsType} props - props
 *  @return {React.FC} Upsell - props
 */
export const Upsell = ({
    cartItem,
    children,
    closeModal,
    dispatch,
    icon = '',
    isAdded,
    isLocked = false,
    isModalVisible,
    name,
    needToShowPrice = true,
    openModal,
    popup,
    price,
    specialPrice = 0,
    title,
    titleStylesForDescription = false,
    toggleUpsell,
}: PropsType) => {
    const [isToggling, setIsToggling] = useState(false);
    const [isTouched, setIsTouched] = useState(false);

    useEffect(() => {
        closeModal();
        setIsToggling(false);
    }, [isAdded, closeModal]);

    useEffect(() => {
        const snackBarRemovedText = title
            ? 'successfully removed.'
            : 'was updated in your shopping cart.';
        const snackBarAddedText = title
            ? 'successfully added.'
            : 'was updated in your shopping cart.';
        if (!isTouched) return;
        pushSnackInfo(
            null,
            `${title || cartItem?.name || ''} ${isAdded ? snackBarAddedText : snackBarRemovedText}`,
            {
                hasAutoClose: true,
                iconType: 'success',
                variant: 'notification',
            },
        );
    }, [isAdded, cartItem?.name, title]);

    const changeUpsell = useCallback(() => {
        if (isLocked) return;
        setIsToggling(true);
        setIsTouched(true);
        toggleUpsell();
        if (name === 'sunglasses')
            sendDesktopAnalyticsEvent(CART_UPGRADE_SAME_FRAME_USAGE, {
                action: isAdded ? 'Remove' : 'Add',
            });
        else
            sendDesktopAnalyticsEvent(CART_UPGRADE_UPGRADES_USAGE, {
                action: isAdded ? 'Remove' : 'Add',
                label: getEventLabel(title, false),
            });
    }, [isAdded, isLocked, title, name, toggleUpsell]);

    const showPopup = useCallback(() => {
        dispatch(setUpsellPopupContent(popup));
        openModal();
        sendDesktopAnalyticsEvent(CART_UPGRADE_UPGRADES_LEARN_MORE, getEventLabel(title, true));
    }, [title, popup, dispatch, openModal]);

    const renderUpsell = () => (
        <div
            className={s.upsellOfferItem}
            data-test-active={isAdded}
            data-test-name="upsell"
            data-test-value={name}
        >
            <Modal
                isOpen={isModalVisible}
                onRequestClose={closeModal}
                overlay="dark"
                zIndex={zIndex.popup}
            >
                {popup}
            </Modal>
            <UpsellContent
                childrenContent={children}
                icon={icon}
                name={name}
                needToShowPrice={needToShowPrice}
                popup={popup}
                price={price}
                showPopup={showPopup}
                specialPrice={specialPrice}
                title={title}
                titleStylesForDescription={titleStylesForDescription}
            />
            <RoundButton
                colorName="codGray"
                isDisabled={false}
                isFull={false}
                isLoading={isToggling}
                onClick={changeUpsell}
                size="small"
                // Added a fragment to avoid the "Failed to execute 'insertBefore' on 'Node'" error,
                // which may occur when translating the page with Google Translate and then clicking the Add button
                startIcon={<>{isAdded ? <Icon icon={Approve} size="small" /> : null}</>}
                variant={isAdded ? 'ghost' : 'painted'}
            >
                {isAdded ? 'Added' : 'Add'}
            </RoundButton>
        </div>
    );

    return <div className={s.tooltipWrapper}>{renderUpsell()}</div>;
};

export const UpsellConnected = connect(
    (store: ApplicationStoreType, {itemId}: Pick<PropsType, 'itemId'>) => ({
        cartItem: itemId ? getCartItemById(store, itemId) : null,
        isLocked: store.cartUpsells.status === 'pending' || store.cart.status === 'pending',
    }),
)(Upsell);
